import React, { type FunctionComponent, useState } from 'react'
import { LoadingDots, useBooleanState } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { type PartnerPickRecurringShiftConflictCheckQuery } from 'domains/partner-pick-devices/api'
import { useDashMessages } from 'intl/intl.hooks'
import { useRecommendedShiftsContext } from 'domains/shifts/components/contexts/RecommendationShiftsContext'
import { ConfirmationModalType } from './PartnerPickRecurringShiftsForm'
import { ConfirmationModal, RadioOptions } from './confirmation-modal/ConfirmationModal'
import {
  StyledCancelButton,
  StyledDeleteButton,
  StyledSubmitButton,
} from './ShiftDetailsForm.styles'

interface ButtonsFooterProps {
  isSubmitEnabled: boolean
  onCancel: () => void
  deleteRecurringShift: () => void
  isEditMode: boolean
  submitForm: () => void
  shopperId?: number
  date?: string
  addNonRecurringShift: () => void
  updateNonRecurringShift: () => void
  getLocalShopperDetails: (shopperId: number) => object
  deleteNonRecurringShift: () => void
  isNonRecurringShift: boolean
  isNonRecurringShiftBeingConvertedToRecurring: boolean
  onlyCommonFieldsDirty: boolean
  zoneOffset: number
  formSubmitLoading: boolean
  shiftDeleteLoading: boolean
  conflictsLoading: boolean
  errorMessage: string
  conflictsData: PartnerPickRecurringShiftConflictCheckQuery
  currentShiftDescription: React.ReactNode
  checkConflicts: () => void
}

const ButtonsContainer = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
})

export const ButtonsFooter: FunctionComponent<ButtonsFooterProps> = ({
  isSubmitEnabled,
  onCancel,
  deleteRecurringShift,
  isEditMode,
  submitForm,
  shopperId,
  date,
  conflictsLoading,
  getLocalShopperDetails,
  deleteNonRecurringShift,
  isNonRecurringShift,
  addNonRecurringShift,
  updateNonRecurringShift,
  zoneOffset,
  formSubmitLoading,
  shiftDeleteLoading,
  errorMessage,
  conflictsData,
  currentShiftDescription,
  isNonRecurringShiftBeingConvertedToRecurring,
  onlyCommonFieldsDirty,
  checkConflicts,
}) => {
  const messages = useDashMessages({
    cancelLabel: 'shiftsDomain.prompt.cancel',
    submitLabel: 'shiftsDomain.prompt.save',
    deleteLabel: 'shiftsDomain.prompt.delete',
  })
  const [confirmationModalType, setConfirmationModalType] = useState<ConfirmationModalType>()
  const [isConfirmationModalOpen, showConfirmationModal, hideConfirmationModal] =
    useBooleanState(false)

  const { refetchRecommendations } = useRecommendedShiftsContext()

  const processDelete = (choice: RadioOptions) => {
    if (choice === RadioOptions.thisAndFollowing) {
      deleteRecurringShift()
    } else {
      deleteNonRecurringShift()
    }
  }

  /// calls conflict check API, shows conflicts and based on
  const _checkConflicts = () => {
    checkConflicts()
    setConfirmationModalType(ConfirmationModalType.Conflicts)
    showConfirmationModal()
  }

  const processEdit = (choice: RadioOptions) => {
    if (choice === RadioOptions.thisAndFollowing) {
      _checkConflicts()
    } else {
      updateNonRecurringShift()
    }
  }

  const onConfirmationModalConfirm = data => {
    if (confirmationModalType === ConfirmationModalType.Delete) {
      processDelete(data)
    }
    // create or update recurring shift
    if (confirmationModalType === ConfirmationModalType.Conflicts) {
      submitForm()
    }
    if (confirmationModalType === ConfirmationModalType.Edit) {
      processEdit(data)
    }
  }

  const onConfirmationModalCancel = () => {
    hideConfirmationModal()
  }

  // This is the initial button handler that decides if we should show conflict check screen
  const handleSubmitClick = () => {
    if (isNonRecurringShiftBeingConvertedToRecurring) {
      _checkConflicts() // and create a new recurring shift from this single shift
      return
    }

    // single shift
    if (isNonRecurringShift) {
      isEditMode ? updateNonRecurringShift() : addNonRecurringShift()
      refetchRecommendations?.()
      return
    }

    // update recurring shift when only common fields are changed (To, From, and Shift Type), prompt user for
    // if the change applies to only this instance or all instances of a recurring shift
    if (isEditMode && onlyCommonFieldsDirty) {
      setConfirmationModalType(ConfirmationModalType.Edit)
      showConfirmationModal()
      refetchRecommendations?.()
      return
    }

    // create recurring shift or update a recurring shift's daysOfWeek or recur_end_date
    _checkConflicts()
  }

  const handleDeleteClick = () => {
    if (isNonRecurringShift) {
      deleteNonRecurringShift()
      return
    }
    setConfirmationModalType(ConfirmationModalType.Delete)
    showConfirmationModal()
  }

  return (
    <React.Fragment>
      <ButtonsContainer>
        {isEditMode && (
          <StyledDeleteButton
            data-testid="shift-details-form-delete"
            onClick={handleDeleteClick}
            variant="detrimental"
            type="button"
            disabled={shiftDeleteLoading}
          >
            {shiftDeleteLoading ? <LoadingDots height={10} /> : messages.deleteLabel}
          </StyledDeleteButton>
        )}
        {!isEditMode && (
          <StyledCancelButton
            data-testid="shift-details-form-cancel"
            onClick={onCancel}
            variant="secondary"
            type="button"
          >
            {messages.cancelLabel}
          </StyledCancelButton>
        )}
        <StyledSubmitButton
          data-testid="shift-details-form-submit"
          type="button"
          disabled={!isSubmitEnabled || shiftDeleteLoading || formSubmitLoading}
          onClick={handleSubmitClick}
        >
          {formSubmitLoading ? <LoadingDots height={10} /> : messages.submitLabel}
        </StyledSubmitButton>
      </ButtonsContainer>
      <ConfirmationModal
        zoneOffset={zoneOffset}
        conflictsLoading={conflictsLoading}
        confirmLoading={formSubmitLoading || shiftDeleteLoading}
        getLocalShopperDetails={getLocalShopperDetails}
        isEditMode={isEditMode}
        date={date}
        shopperId={shopperId}
        singleShiftConflicts={
          conflictsData?.partnerPickRecurringShiftConflictCheck?.singleShiftConflicts
        }
        recurringShiftConflicts={
          conflictsData?.partnerPickRecurringShiftConflictCheck?.recurringShiftConflicts
        }
        modalType={confirmationModalType}
        isOpen={isConfirmationModalOpen}
        onConfirm={onConfirmationModalConfirm}
        onCancel={onConfirmationModalCancel}
        errorMessage={errorMessage}
        currentShiftDescription={currentShiftDescription}
      />
    </React.Fragment>
  )
}
